/**
 * Projection support for ol-ishare and ol.
 * @module ol-ishare/proj
 */

import * as olProj from 'ol/proj';
import { register } from 'ol/proj/proj4';

let _proj4;

/**
 * ```
 * import { addProjection } from 'ol-ishare/proj.js';
 * ```
 * Adds a projection to ol-ishare and ol using proj4 if available.
 *
 * proj4 can be registered via calling {@link setProj4} or by exposing proj4 at
 * `window.proj4`.
 * @param {String} code ID of the projection e.g. `'EPSG:27700'`
 * @param {String} definition Projection definition in wkt or proj4 format e.g. `'+title=British National Grid +proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy +datum=OSGB36 +units=m +no_defs'`
 * @returns {ol/proj/Projection~Projection} OpenLayers Projection instance
 */
function addProjection(code, definition) {
  const proj4 = getProj4();
  if (proj4) {
    console.info('Found proj4, creating official projection...');
    proj4.defs(code, definition);
    // Register current proj4 projection definitions with OpenLayers, must
    // be called whenever changes are made to the proj4 registry, e.g.
    // after calling proj4.defs()
    register(proj4);
  }
  let projection = olProj.get(code);
  if (projection === null) {
    console.info(
      'Could not find proj4, or built in projection, creating basic projection'
    );
    projection = new olProj.Projection({
      code: code,
      units: (definition.match(/\+units=(\w+)/) || ['', 'm'])[1]
    });
    olProj.addProjection(projection);
  }
  return projection;
}

/**
 * ```
 * import { setProj4 } from 'ol-ishare/proj.js';
 * ```
 * Set the proj4 instance that ol-ishare and ol will use to define custom
 * projections.
 *
 * Use {@link addProjection} to add a projection to ol-ishare and ol.
 * @param {Object} proj4 The proj4 instance
 * @returns {Object|undefined} The proj4 instance or undefined
 */
function setProj4(proj4) {
  if (proj4) {
    _proj4 = proj4;
    // Register current proj4 projection definitions with OpenLayers, register
    // must be called whenever changes are made to the proj4 registry, e.g.
    // after calling proj4.defs()
    register(_proj4);
  }
  return _proj4;
}

/**
 * ```
 * import { getProj4 } from 'ol-ishare/proj.js';
 * ```
 * Attempt to get a reference to the proj4 instance used by ol-ishare and ol to
 * define custom projections. If proj4 is not already registered, this function
 * will attempt to find a reference at `window.proj4`.
 *
 * Use {@link addProjection} to add a projection to ol-ishare and ol.
 * @returns {Object|undefined} The proj4 instance or undefined
 */
function getProj4() {
  if (!_proj4 && window.proj4) {
    return setProj4(window.proj4);
  }
  return _proj4;
}

export { addProjection, setProj4, getProj4 };
